import dayjs from "dayjs";
import React from "react";
import {
  ChartComponentProps,
  ChartConfig,
  createChartConfig,
  TimeSeriesDataPoint,
} from "src/components/Referral-partner/Analytics/analyticsTypes";
import DepositVolume from "src/components/Referral-partner/Analytics/charts/DepositVolume";
import { WarningWrapper } from "src/components/Referral-partner/Analytics/styles/shared.styles";
import api from "src/store/features/api";
import { useRPAnalyticsState } from "src/store/features/RPAnalytics.slice";

const useGetDepositVolumeConfig = (): {
  depositVolume: ChartConfig<TimeSeriesDataPoint>;
} => {
  const { chartDateRange } = useRPAnalyticsState();

  // fetch data and states
  const {
    data: rawDepositVolume,
    isLoading: depositIsLoading,
    isFetching: depositIsFetching,
    isError: depositIsError,
    error: depositError,
  } = api.useGetDepositsVolumeQuery({
    timeFrame: chartDateRange.depositVolume,
    timezone: dayjs.tz.guess(),
  });

  // format data
  const depositVolumeData = React.useMemo((): TimeSeriesDataPoint[] => {
    if (!rawDepositVolume) return [];
    return rawDepositVolume.data.filter(
      (entry) => entry["Crypto Deposits"] !== 0 || entry["Fiat Deposits"] !== 0,
    ) as TimeSeriesDataPoint[];
  }, [rawDepositVolume]);

  // create chart config
  const depositVolumeConfig = React.useMemo(
    (): {
      depositVolume: ChartConfig<TimeSeriesDataPoint>;
    } => ({
      depositVolume: createChartConfig<TimeSeriesDataPoint>({
        title: "Deposits by Volume",
        type: "bar",
        menuMode: "normal",
        defaultDate: "all time",
        tooltip: "",
        isLoading: depositIsLoading || depositIsFetching,
        data: depositVolumeData,
        component: ({ data }: ChartComponentProps<TimeSeriesDataPoint>): JSX.Element => {
          if (depositIsError) {
            return <WarningWrapper>{depositError?.message}</WarningWrapper>;
          } else if (data.length <= 0) {
            return <WarningWrapper color="#002035">No Data</WarningWrapper>;
          } else {
            return <DepositVolume data={data} />;
          }
        },
      }),
    }),
    [depositVolumeData, depositIsLoading, depositIsFetching, depositIsError, depositError],
  );

  return depositVolumeConfig;
};

export default useGetDepositVolumeConfig;
